import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { forkJoin, map, Subscription } from 'rxjs';
import {
  CreateCustomerPreparationFromOrderLinesRequestDto,
  CreateCustomerPreparationFromOrderLinesResultDto,
  CustomerOrderLineToPrepareDto,
  CustomerPreparationDto,
  CustomerPreparationService,
  DocLineRole,
  EligibleCustomerOrderForPreparationDto,
} from '@digiwall/goliath-documents/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import { EligibleCustomerOrdersGridComponent } from './eligible-customer-orders-grid/eligible-customer-orders-grid.component';
import { SharedModule } from '@digiwall/goliath-core';
import { CustomerPreparationLinesEditorComponent } from './customer-preparation-lines-editor/customer-preparation-lines-editor.component';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Confirmation, ConfirmationService, DEFAULT_ERROR_LOCALIZATIONS } from '@abp/ng.theme.shared';
import { DocRole } from '@digiwall/goliath-core/proxy';
import { PreparationNavigationService } from '../../../../components';

@Component({
  selector: 'document-customer-preparation-creation-dialog',
  templateUrl: './customer-preparation-creation-dialog.component.html',
  styleUrl: './customer-preparation-creation-dialog.component.scss',
  imports: [
    DialogBoxComponent,
    CommonModule,
    SharedModule,
    EligibleCustomerOrdersGridComponent,
    CustomerPreparationLinesEditorComponent,
  ],
})
export class CustomerPreparationCreationDialogComponent implements OnInit, OnDestroy {
  @Input() customerSalePurchaseId!: string;

  /** `null` until the fetch resolves, so the "nothing to prepare" state is never flashed. */
  protected readonly eligibleOrders = signal<EligibleCustomerOrderForPreparationDto[] | null>(null);
  /** Nothing eligible means nothing to prepare: the line editor is left out of the dialog entirely. */
  protected readonly hasEligibleOrders = computed(() => (this.eligibleOrders()?.length ?? 0) > 0);
  selectedOrders: EligibleCustomerOrderForPreparationDto[] = [];
  orderLinesToPrepare: CustomerOrderLineToPrepareDto[] = [];
  preparation: CustomerPreparationDto | null = null;
  isSaving = false;

  private orderLinesCache = new Map<string, CustomerOrderLineToPrepareDto[]>();
  private fetchSubscription: Subscription | null = null;

  customerPreparationService = inject(CustomerPreparationService);
  private readonly activeCanvas = inject(NgbActiveOffcanvas);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly preparationNavigation = inject(PreparationNavigationService);

  constructor(private localizationService: LocalizationService) {}

  ngOnInit() {
    this.customerPreparationService
      .getEligibleCustomerOrders(this.customerSalePurchaseId)
      .subscribeOnce(orders => this.eligibleOrders.set(orders ?? []));
  }

  onOrderSelectionChange(selectedOrders: EligibleCustomerOrderForPreparationDto[]) {
    this.selectedOrders = selectedOrders ?? [];

    this.fetchSubscription?.unsubscribe();
    this.fetchSubscription = null;

    if (!this.selectedOrders.length) {
      this.orderLinesToPrepare = [];
      return;
    }

    const linesFromCache: CustomerOrderLineToPrepareDto[] = [];
    const ordersToFetch: EligibleCustomerOrderForPreparationDto[] = [];

    for (const order of this.selectedOrders) {
      if (!order.id) {
        continue;
      }

      if (this.orderLinesCache.has(order.id)) {
        linesFromCache.push(...(this.orderLinesCache.get(order.id) ?? []));
      } else {
        ordersToFetch.push(order);
      }
    }

    this.orderLinesToPrepare = [...linesFromCache];

    if (!ordersToFetch.length) return;

    this.fetchSubscription = forkJoin(
      ordersToFetch.map(o =>
        this.customerPreparationService.getCustomerOrderLinesToPrepare(o.id!).pipe(
          map(lines => ({
            orderId: o.id!,
            lines: (lines ?? []).map(l => ({ ...l, orderDisplayNumber: o.displayNumber })),
          })),
        ),
      ),
    ).subscribe(results => {
      const newLines: CustomerOrderLineToPrepareDto[] = [];

      results.forEach(({ orderId, lines }) => {
        lines.forEach(line => {
          line.isSelectedByDefault =
            line.selectedQuantity > 0 &&
            line.remainingQuantity > 0 &&
            line.customerSalePurchaseId === this.customerSalePurchaseId;
        });
        this.orderLinesCache.set(orderId, lines);
        newLines.push(...lines);
      });

      this.orderLinesToPrepare = [...this.orderLinesToPrepare, ...newLines];
    });
  }

  ngOnDestroy(): void {
    this.fetchSubscription?.unsubscribe();
  }

  save() {
    if (this.isSaving || !this.orderLinesToPrepare?.length) return;

    const selectedLines = this.orderLinesToPrepare.filter(
      l =>
        (l.selectedQuantity ?? 0) > 0 &&
        !!l.id &&
        !!l.sourceLineId &&
        !!l.customerSalePurchaseId,
    );
    if (!selectedLines.length) {
      return;
    }

    const request: CreateCustomerPreparationFromOrderLinesRequestDto = {
      lines: selectedLines.map(line => ({
        id: line.id,
        sourceLineId: line.sourceLineId,
        customerSalePurchaseId: line.customerSalePurchaseId,
        lineRole: line.lineRole,
        selectedQuantity: line.selectedQuantity,
        remainingQuantity: line.remainingQuantity,
        stockLocationId: line.stockLocationId,
        articleSerialId: line.articleSerialId,
        articleLotId: line.articleLotId,
        articleRangeValue1Id: line.articleRangeValue1Id,
        articleRangeValue2Id: line.articleRangeValue2Id,
        backorderMvtId: line.backorderMvtId,
        reservationMvtId: line.reservationMvtId,
      })),
    };

    this.isSaving = true;
    this.customerPreparationService.createFromOrderLines(request).subscribeOnce(
      result => {
        this.isSaving = false;
        this.showResultSummary(result);
        this.activeCanvas.close(result);
      },
      error => {
        this.isSaving = false;
        this.showServerError(error);
      },
    );
  }

  private showResultSummary(result: CreateCustomerPreparationFromOrderLinesResultDto) {
    const successMessage = this.localizationService.instant(
      'Doc::CustomerPreparation:GenerationSummary',
      result.createdPreparationsCount.toString(),
      result.createdLinesCount.toString(),
      result.impactedOrdersCount.toString(),
    );
    const detailedMessages = result.errors
      .map(error => error.message?.trim())
      .filter((message): message is string => !!message);

    const dialogLines: string[] = [successMessage];
    if ((result.errors?.length ?? 0) > 0) {
      const errorMessage = this.localizationService.instant(
        'Doc::CustomerPreparation:GenerationErrorsCount',
        result.errors.length.toString(),
      );
      dialogLines.push('');
      dialogLines.push(errorMessage);

      if (detailedMessages.length > 0) {
        dialogLines.push(...[...new Set(detailedMessages)].map(message => `- ${message}`));
      }
    }

    const hasErrors = (result.errors?.length ?? 0) > 0;
    const title = {
      key: hasErrors ? '::Warning' : '::Information',
      defaultValue: hasErrors ? 'Warning' : 'Information',
    };

    const message = {
      key: dialogLines.join('\n'),
      defaultValue: dialogLines.join('\n'),
    };

    // Anything generated turns the summary into a way in: confirming opens the new preparation,
    // asking which one when the generation produced several.
    const canOpen = this.preparationNavigation.hasGenerated(result.createdPreparations);
    const options = canOpen
      ? {
          yesText: 'Doc::CustomerPreparation:OpenGeneratedPreparation',
          cancelText: 'AbpAccount::Close',
        }
      : {
          hideCancelBtn: true,
          yesText: 'AbpAccount::Close',
        };

    const summary = hasErrors
      ? this.confirmationService.warn(message, title, options)
      : this.confirmationService.info(message, title, options);

    if (!canOpen) return;

    summary.subscribeOnce(status => {
      if (status === Confirmation.Status.confirm) {
        this.preparationNavigation.openGenerated(
          result.createdPreparations,
          DocRole.CustomerPreparation,
        );
      }
    });
  }

  private showServerError(error: unknown) {
    const remoteError = (error as any)?.error?.error;
    const remoteCode = remoteError?.code as string | undefined;
    const remoteMessage = remoteError?.message as string | undefined;

    if (remoteCode) {
      const localized = this.localizationService.instant(remoteCode);
      if (localized && localized !== remoteCode) {
        this.confirmationService.error(
          { key: localized, defaultValue: localized },
          {
            key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
            defaultValue: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
          },
          {
            hideCancelBtn: true,
            yesText: 'AbpAccount::Close',
          },
        );
        return;
      }
    }

    if (remoteMessage?.trim()) {
      this.confirmationService.error(
        { key: remoteMessage, defaultValue: remoteMessage },
        {
          key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
          defaultValue: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
        },
        {
          hideCancelBtn: true,
          yesText: 'AbpAccount::Close',
        },
      );
      return;
    }

    const fallback = this.localizationService.instant('AbpUi::DefaultErrorMessage');
    const fallbackMessage =
      fallback && fallback !== 'AbpUi::DefaultErrorMessage'
        ? fallback
        : 'An unexpected error occurred.';

    this.confirmationService.error(
      {
        key: fallbackMessage,
        defaultValue: fallbackMessage,
      },
      {
        key: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
        defaultValue: DEFAULT_ERROR_LOCALIZATIONS.defaultError.title,
      },
      {
        hideCancelBtn: true,
        yesText: 'AbpAccount::Close',
      },
    );
  }

  public get dialogBoxTitle() {
    return this.localizationService.instant('Doc::CustomerSalesPurchase:GeneratePreparation');
  }

  public get canSave() {
    return this.orderLinesToPrepare.some(
      line =>
        (line.selectedQuantity ?? 0) > 0 &&
        !!line.id &&
        !!line.sourceLineId &&
        !!line.customerSalePurchaseId &&
        line.lineRole !== DocLineRole.CustomerDocText,
    );
  }
}
